import DonutLargeRoundedIcon from '@mui/icons-material/DonutLargeRounded';
import DonutSmallRoundedIcon from '@mui/icons-material/DonutSmallRounded';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import {
	Cell,
	Label,
	Legend,
	Pie,
	PieChart,
	Sector,
	Tooltip
} from 'recharts';
import { usePrerequisites } from '../../../contexts/Prerequisites';
import { getColors } from './colors';

const RADIAN = Math.PI / 180;

const PieChartActiveShape = ({
	cx,
	cy,
	midAngle,
	innerRadius,
	outerRadius,
	startAngle,
	endAngle,
	fill,
	payload,
	percent,
	index,
	value
}) => {
	const sin = Math.sin(-RADIAN * midAngle);
	const cos = Math.cos(-RADIAN * midAngle);
	const sx = cx + (outerRadius + 10) * cos;
	const sy = cy + (outerRadius + 10) * sin;
	const mx = cx + (outerRadius + 30) * cos;
	const my = cy + (outerRadius + 30) * sin;
	const ex = mx + (cos >= 0 ? 1 : -1) * 22;
	const ey = my;
	const textAnchor = cos >= 0 ? 'start' : 'end';
	const label = payload?.label || '';

	return (
		<g>
			<text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill} >
				{payload.name}
			</text>
			<Sector
				cx={cx}
				cy={cy}
				innerRadius={innerRadius}
				outerRadius={outerRadius}
				startAngle={startAngle}
				endAngle={endAngle}
				fill={fill}
			/>
			<Sector
				cx={cx}
				cy={cy}
				startAngle={startAngle}
				endAngle={endAngle}
				innerRadius={outerRadius + 6}
				outerRadius={outerRadius + 10}
				fill={fill}
			/>
			<path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
			<circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
			<text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill={fill}>{`${label} (${value})`}</text>
			<text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
				{`${(percent * 100).toFixed(2)}%`}
			</text>
		</g>
	);
};



const CustomPieChart = forwardRef(({
	data,
	hideLegend,
	onMouseEnter,
	onMouseLeave,
	label,
	labelProps,
	legendProps,
	onClick,
	width = '100%',
    height = 300,
	showTooltip,
	children,
	...rest
}, ref) => {
	const theme = useTheme();
	const [activeIndex, setActiveIndex] = useState(-1);
	const [{strings}] = usePrerequisites();
	const handleOnClick = useCallback(
		(e, i) => {
			if (activeIndex === i) {
				setActiveIndex(-1);
			} else {
				setActiveIndex(i);
			}
			if (typeof onClick === 'function') {
				onClick(e, i);
			}
		},
		[activeIndex, onClick]
	);
	const handleOnMouseEnter = useCallback(
		(e, i) => {
			setActiveIndex(i);
			if (typeof onMouseEnter === 'function') {
				onMouseEnter(e, i);
			}
		},
		[onMouseEnter]
	);
	const handleOnMouseLeave = useCallback(
		(e, i) => {
			if (typeof onMouseLeave === 'function') {
				onMouseLeave(e, i);
			}
		},
		[onMouseLeave]
	);

	
	

	const { dataset, colors } = useMemo(() => {
		const chartDataset = Array.isArray(data)
			? data.sort((a, b) => (a?.label < b?.label ? -1 : a?.label > b?.label? 1 : 0))
			: [];
		return { dataset: chartDataset, colors: getColors(chartDataset.length) };
	}, [data])

	const renderLegend = (props) => {
		const { payload } = props;
		return (
			<Box gap={1} display="flex" flexWrap={'wrap'} justifyContent={'center'}>
				{payload.map((entry, index) => (
					<Box display="flex" alignItems={'center'} gap={0.5} color={entry.color} key={`legend-item-${index}`}>
						<DonutSmallRoundedIcon fontSize="inherit" />
						<Typography variant="body2" color="textPrimary" key={`item-${index}`}>
							{entry.value}
						</Typography>
					</Box>
				))}
			</Box>
		);
	};
	return dataset?.length > 0 ? (
		<Box
				sx={{
					overflowY: 'hidden',
					overflowX: 'auto',
					'& .recharts-wrapper': {
						margin: 'auto'
					}
				}}
				width={'100%'}
			>
			<PieChart ref={ref} height={height} width={500}>
				<Pie
					data={dataset}
					activeIndex={activeIndex}
					activeShape={PieChartActiveShape}
					cx="50%"
					cy="50%"
					innerRadius={70}
					outerRadius={100}
					fill={theme.palette.primary.dark}
					dataKey="value"
					nameKey="label"
					style={{ fontSize: 12 }}
					label={false}
					{...rest}
					onMouseEnter={handleOnMouseEnter}
					onMouseLeave={handleOnMouseLeave}
					onClick={handleOnClick}
					isAnimationActive={false}
				>
					{!!label && (
						<Label position="center" fontSize={`13px`} fontWeight="bold" {...labelProps} value={label} />
					)}
					{dataset.map((item, i) => (
						<Cell fill={colors[i]} key={`pie-cell-${i}`} />
					))}
					{children}
				</Pie>
				{showTooltip && <Tooltip />}
				{!hideLegend && (
					<Legend wrapperStyle={{ maxHeight: '30%', overflowY: 'auto' }} content={renderLegend} {...legendProps} />
				)}
			</PieChart>
		</Box>
	) : (
		<Box
			height={height}
			display="flex"
			alignItems="center"
			color="text.disabled"
			flexDirection={'column'}
			justifyContent="center"
		>
			<Typography variant="h1">
				<DonutLargeRoundedIcon fontSize="inherit" />
			</Typography>
			<Typography variant="h5">{strings.NoRecordFound || 'No Record found'}</Typography>
		</Box>
	);
});


export default memo(CustomPieChart);